import { FC } from 'react';

import defaultImage from 'assets/images/banner.jpeg';

// Quarks
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';

import type { ContentfulResourceCard } from 'graphqlTypes';

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const formatDate = dt => {
  const options = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  };
  const formattedDate = new Date(dt).toLocaleDateString(undefined, options);

  return formattedDate;
};

const WebinarCard: FC<ContentfulResourceCard> = ({
  featuredImage,
  seo,
  title,
  date,
  slug,
  summary,
  backgroundIsDark,
}) => {
  const href = `/webinar/${slug}`;

  return (
    <Link href={href} textDecoration="none" width="100%">
      <Flex
        width="100%"
        height="100%"
        flexDirection="column"
        borderRadius="0"
        overflowX="hidden"
        overflowY="hidden"
        border="1px solid"
        borderColor="gray-200"
        hover={{ boxShadow: 'xxl' }}
        transition="200ms ease-in-out"
        backgroundColor={backgroundIsDark ? 'gray-900' : 'common-white'}
      >
        <div className="w-full aspect-[16/9] overflow-hidden">
          {seo?.openGraphImage || featuredImage ? (
            <Image
              image={seo?.openGraphImage || featuredImage}
              alt={title}
              objectFit="cover"
              objectPosition="center"
              width="100%"
              height="100%"
            />
          ) : (
            <Image
              image={{ file: { url: defaultImage } }}
              alt="Card Image"
              objectFit="cover"
              objectPosition="center"
              width="100%"
              height="100%"
            />
          )}
        </div>
        <Flex
          position="relative"
          flexDirection="column"
          justifyContent="flex-start"
          gap="16px"
          height="100%"
          maxHeight="fit-content"
          css={`
            padding: 32px 32px 96px 32px;
          `}
        >
          <Flex flexDirection="column" gap="2px">
            <Heading
              as="h4"
              fontSize="textXs"
              data-cy="title"
              lineHeight="textXl"
              textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
              textTransform="uppercase"
            >
              AIRED ON {formatDate(date)}
            </Heading>
            <Heading
              as="h4"
              fontSize="textMd"
              lineHeight="textXl"
              data-cy="title"
              textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
            >
              {title}
            </Heading>
            {summary && (
              <Text
                fontSize="textSm"
                lineHeight="textXxl"
                textColor={backgroundIsDark ? 'gray-200' : 'gray-900'}
                css={textWrapCSS}
              >
                {richTextParser(summary)}
              </Text>
            )}
          </Flex>
          <Flex gap="4px" alignItems="center" position="absolute" bottom="20px" paddingAll={0}>
            <ComponentButton
              link={href}
              label="Watch Now"
              buttonColor="Gray 900 Link"
              css={{
                fontSize: '14px !important',
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    </Link>
  );
};

export default WebinarCard;
